import * as React from 'react';
import { useSelector } from 'react-redux';
import InputNumber from 'antd/lib/input-number';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import { EditOutlined } from '@ant-design/icons';
import { Content } from 'antd/lib/layout/layout';
import { RootState } from '../../reducers/reducers';
import { selectOrgSettings } from '../../selectors/selectors';
import { OrgComponentProps } from '../../typescriptHelpers';
import { useUpdateBuildDistroSettingsMutation } from '@/middlewares/defaultApi';
import { globalMessage } from '@/utils/globalMessage';
import {
    BuildDistroPublicURLExpiration,
    BuildDistroPublicURLExpirationMode,
    BuildDistroPublicURLExpirationUnit,
    OrgSettings,
} from '@/types';

const nonNoneUnitOptions: { label: string; value: BuildDistroPublicURLExpirationUnit }[] = [
    { label: 'days', value: BuildDistroPublicURLExpirationUnit.Days },
    { label: 'weeks', value: BuildDistroPublicURLExpirationUnit.Weeks },
    { label: 'months', value: BuildDistroPublicURLExpirationUnit.Months },
    { label: 'years', value: BuildDistroPublicURLExpirationUnit.Years },
];

const defaultExpiration: BuildDistroPublicURLExpiration = {
    mode: BuildDistroPublicURLExpirationMode.None,
    amount: null,
    unit: null,
};

const rowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    flex: 1,
    minHeight: 80,
};

interface FormValues {
    publicBuildExpiration: BuildDistroPublicURLExpiration;
    publicBucketExpiration: BuildDistroPublicURLExpiration;
}

const ExpirationFormGroup: React.FC<{
    name: keyof FormValues;
    label: string;
}> = ({ name, label }) => {
    const form = Form.useFormInstance<FormValues>();
    const mode = Form.useWatch([name, 'mode'], form);

    return (
        <div
            style={{
                marginBottom: 16,
            }}
        >
            <Form.Item name={[name, 'mode']}>
                <Radio.Group value={mode} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <Radio
                        value={BuildDistroPublicURLExpirationMode.None}
                    >{`Public ${label} links do not expire`}</Radio>
                    <div>
                        <Radio
                            value={BuildDistroPublicURLExpirationMode.Expires}
                        >{`Public ${label} links expire in...`}</Radio>
                        <div style={{ marginTop: 8, marginLeft: 24 }}>
                            <Space size="small">
                                <Form.Item
                                    name={[name, 'amount']}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (mode !== BuildDistroPublicURLExpirationMode.Expires) {
                                                    return Promise.resolve();
                                                }
                                                if (!value) {
                                                    return Promise.reject('Amount is required');
                                                }
                                                if (value <= 0) {
                                                    return Promise.reject('Amount must be a positive number');
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <InputNumber
                                        min={1}
                                        disabled={mode !== BuildDistroPublicURLExpirationMode.Expires}
                                        style={{ width: 80 }}
                                    />
                                </Form.Item>
                                <Form.Item
                                    name={[name, 'unit']}
                                    rules={[
                                        {
                                            validator: (_, value) => {
                                                if (mode !== BuildDistroPublicURLExpirationMode.Expires) {
                                                    return Promise.resolve();
                                                }
                                                if (!value) {
                                                    return Promise.reject('Unit is required');
                                                }
                                                return Promise.resolve();
                                            },
                                        },
                                    ]}
                                >
                                    <Select
                                        style={{ width: 120 }}
                                        disabled={mode !== BuildDistroPublicURLExpirationMode.Expires}
                                        options={nonNoneUnitOptions}
                                    />
                                </Form.Item>
                            </Space>
                        </div>
                    </div>
                </Radio.Group>
            </Form.Item>
        </div>
    );
};

const BuildDistroSettings: React.FC<OrgComponentProps> = ({ orgId }) => {
    const orgSettings = useSelector((state: RootState) => selectOrgSettings(state, orgId) || ({} as OrgSettings));
    const [form] = Form.useForm<FormValues>();
    const [modalOpen, setModalOpen] = React.useState(false);
    const [updateBuildDistroSettings, { isLoading, data: updateSettingsResponse }] =
        useUpdateBuildDistroSettingsMutation();
    const updateSettingsSuccess = updateSettingsResponse?.success;
    const updateSettingsError = updateSettingsResponse?.errorMessage;

    const publicBuildExpiration = orgSettings.buildDistroSettings?.publicBuildExpiration ?? defaultExpiration;
    const publicBucketExpiration = orgSettings.buildDistroSettings?.publicBucketExpiration ?? defaultExpiration;

    const openModal = () => {
        form.setFieldsValue({
            publicBuildExpiration,
            publicBucketExpiration,
        });
        setModalOpen(true);
    };

    const onSubmit = (values: FormValues) =>
        updateBuildDistroSettings({
            orgId,
            settings: {
                publicBuildExpiration: values.publicBuildExpiration,
                publicBucketExpiration: values.publicBucketExpiration,
            },
        });

    React.useEffect(() => {
        if (updateSettingsSuccess) {
            globalMessage.success({
                content: 'Build Distro settings updated!',
                key: 'buildDistroSettingsUpdated',
            });
            setModalOpen(false);
        }
    }, [updateSettingsSuccess]);

    React.useEffect(() => {
        if (updateSettingsError) {
            globalMessage.error(updateSettingsError);
        }
    }, [updateSettingsError]);

    return (
        <Content>
            <div style={{ maxWidth: 900, width: '100%', marginTop: 12 }}>
                <div style={rowStyle}>
                    <Space direction="vertical" size={4}>
                        <Text strong>Expiration for public build and bucket links</Text>
                        <Text type="secondary">
                            Set how long shared builds and buckets remain accessible from a given link.
                        </Text>
                    </Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={openModal}>
                        Edit
                    </Button>
                </div>
            </div>

            <Modal
                title="Edit Build Distro expiration defaults"
                open={modalOpen}
                okText="Save"
                cancelButtonProps={{ disabled: isLoading }}
                okButtonProps={{ loading: isLoading }}
                onOk={() => form.submit()}
                onCancel={() => setModalOpen(false)}
                afterClose={() => form.resetFields()}
            >
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Text>
                        Set how long shared links remain valid. After expiration, build and bucket links become
                        inaccessible to recipients — testers won't be able to download builds.
                    </Text>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={{ publicBuildExpiration, publicBucketExpiration }}
                        onFinish={onSubmit}
                        style={{ marginTop: 24, width: '100%' }}
                    >
                        <ExpirationFormGroup name="publicBuildExpiration" label="build" />
                        <ExpirationFormGroup name="publicBucketExpiration" label="bucket" />
                    </Form>
                </div>
            </Modal>
        </Content>
    );
};

export default BuildDistroSettings;
